import { WarehouseSettings } from 'app/model/dataflow/dataset-settings/warehouse-settings';
import { Dataset, LinkedService, Metamodel, DataFlowDSLAdapterRegistry, Owned } from 'shell-models-lib';
import { BaseSqlFormatAdapter } from './sql-base.adapter';
import { DataFlowExpressionUtils } from '../expression.util';
import { IntegerDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DatasetUtils } from 'app/model/dataset/dataset.utils';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowAdapterUtils } from '../adapter.util';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(WarehouseSettings)
})
export class WarehouseFormatAdapter extends BaseSqlFormatAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined, format?: string): void {
        if (linkedService) {
            const dbName = DatasetUtils.getDatabaseFromLinkedService(linkedService);
            if (dbName) {
                props.push(`database: ${DataFlowAdapterUtils.formatExpressionValue(dbName)}`);
            }
        }

        if (isInline || !dataset) {
            return;
        }

        props.push(`store: '${DatasetFormatType.Warehouse}'`);

        DataFlowAdapterUtils.supplementSqlSchemaAndTableFromDataset(dataset, props, isSource, format);
    }

    protected async toSourceDSL(warehouseSettings: WarehouseSettings): Promise<string[]> {
        const props = await super.toSourceDSL(warehouseSettings);
        return props.concat(this._commonToWarehouseDSL(warehouseSettings));
    }

    protected async toSinkDSL(warehouseSettings: WarehouseSettings): Promise<string[]> {
        const props = await super.toSinkDSL(warehouseSettings);
        if(warehouseSettings.staged && warehouseSettings.useCopyCommandForStaging) {
            props.push(`allowCopyCommand: true`);
        }
        return props.concat(this._commonToWarehouseDSL(warehouseSettings));
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[],
        settings?: WarehouseSettings
    ): Promise<WarehouseSettings> {
        if(settings) {
            await super.toSourceModel(owner, configs, handledConfigs, settings);
            return this._commonToWarehouseModel(owner, configs, handledConfigs, settings);
        }
        const warehouseSettings = Owned.create<WarehouseSettings>(WarehouseSettings.MODEL, owner);
        warehouseSettings.initialize(undefined);
        await super.toSourceModel(owner, configs, handledConfigs, warehouseSettings);
        return this._commonToWarehouseModel(owner, configs, handledConfigs, warehouseSettings);
    }

    protected async toSinkModel(owner: DataFlowSink, configs: Map<string, string>, handledConfigs: string[], settings?: WarehouseSettings): Promise<WarehouseSettings> {
        if(settings) {
            settings.useCopyCommandForStaging = configs.has('allowCopyCommand') && configs.get('allowCopyCommand') === 'true';
            handledConfigs.push('allowCopyCommand');
            await super.toSinkModel(owner, configs, handledConfigs, settings);
            return this._commonToWarehouseModel(owner, configs, handledConfigs, settings);
        }
        const warehouseSettings = Owned.create<WarehouseSettings>(WarehouseSettings.MODEL, owner);
        warehouseSettings.initialize(undefined);
        warehouseSettings.useCopyCommandForStaging = configs.has('allowCopyCommand') && configs.get('allowCopyCommand') === 'true';
        handledConfigs.push('allowCopyCommand');
        await super.toSinkModel(owner, configs, handledConfigs, warehouseSettings);
        return this._commonToWarehouseModel(owner, configs, handledConfigs, warehouseSettings);
    }
    
    private _commonToWarehouseModel(owner: DataFlowSource | DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[],
        warehouseSettings: WarehouseSettings
        ): WarehouseSettings {
            if(configs.has('staged')) {
                warehouseSettings.staged = configs.get('staged') === 'true';
                handledConfigs.push('staged');
            }
            if(configs.has('batchSize')) {
                warehouseSettings.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('batchSize'),
                    owner,
                    IntegerDataFlowType.TYPE
                );
                handledConfigs.push('batchSize');
            }
            return warehouseSettings;
        }

    private _commonToWarehouseDSL(warehouseSettings: WarehouseSettings): string[] {
        const props = [];
        props.push(`staged: ${warehouseSettings.staged}`);
        if (warehouseSettings.batchSize.expression && warehouseSettings.staged) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    warehouseSettings.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        return props;
    }
}
